import React from 'react';

import Icon from '@/components/ui/AppIcon';
import AppImage from '@/components/ui/AppImage';

interface TransformationPathwayProps {
  className?: string;
}

const TransformationPathway: React.FC<TransformationPathwayProps> = ({ className = '' }) => {
  return (
    <section className={`pt-8 pb-8 bg-gradient-to-br from-primary/5 to-accent/5 ${className}`}>
      <div className="container mx-auto px-4">
        <div className="max-w-5xl mx-auto">
          {/* Main Message */}
          <div className="text-center mb-16">
            <h2 className="font-display font-bold text-3xl lg:text-5xl text-foreground mb-6 leading-tight">
              One system. Many interactions. Real business outcomes.
            </h2>
            <p className="text-lg text-muted-foreground mt-4 max-w-4xl mx-auto">
              UnifyMax synchronizes sales, support, digital, video, and physical touchpoints - turning every interaction into a driver of real business outcomes: revenue growth, higher customer lifetime value, and repeat purchases.
            </p>
          </div>

          {/* Flow Chart Heading */}
          <div className="text-center mb-6">
            <h3 className="font-display font-bold text-2xl lg:text-3xl text-foreground leading-tight">AI-led Transformation Architecture to Deliver Real Business Outcomes​

            </h3>
          </div>

          {/* Comprehensive Organizational Diagram */}
          <div className="bg-gradient-to-br from-slate-100 via-[#276A89]/10 to-orange-50 rounded-2xl p-4 lg:p-8 shadow-lg border border-border mb-12 overflow-hidden">
            <div className="grid grid-cols-1 lg:grid-cols-[1fr_auto] gap-6">
              {/* Main Content Area */}
              <div className="space-y-2.5">
                {/* Engagement Section */}
                <div className="bg-white/80 backdrop-blur rounded-xl p-2.5 border-2 shadow-md border-[rgba(26,180,216,1)]">
                  <div className="flex flex-col lg:flex-row gap-2.5">
                    <div className="lg:w-44 flex-shrink-0">
                      <h3 className="font-display font-bold text-base text-slate-900 mb-0.5">Engagement</h3>
                      <p className="text-xs text-[#276A89] font-medium">MaxAI + Phygitalmax</p>
                    </div>
                    <div className="flex-1 grid grid-cols-1 md:grid-cols-2 gap-2">
                      <div className="bg-[#0F93B2]/10 rounded-lg p-2 border border-[#276A89]/50">
                        <h4 className="font-semibold text-xs text-slate-800 mb-1 text-center">Human Workforce</h4>
                        <div className="space-y-1">
                          <div className="bg-[#0F93B2]/20 rounded px-2 py-1 text-xs text-center font-medium text-slate-800">Agent Assist Cockpit</div>
                          <div className="bg-[#0F93B2]/20 rounded px-2 py-1 text-xs text-center font-medium text-slate-800">Human in the loop</div>
                          <div className="bg-[#0F93B2]/20 rounded px-2 py-1 text-xs text-center font-medium text-slate-800">Continuous Learning & Feedback Loop</div>
                        </div>
                      </div>
                      <div className="bg-[#0F93B2]/10 rounded-lg p-2 border border-[#276A89]/50">
                        <h4 className="font-semibold text-xs text-slate-800 mb-1 text-center">AI Workforce</h4>
                        <div className="space-y-1">
                          <div className="bg-[#0F93B2]/20 rounded px-2 py-1 text-xs text-center font-medium text-slate-800">Agentic AI Agents</div>
                          <div className="bg-[#0F93B2]/20 rounded px-2 py-1 text-xs text-center font-medium text-slate-800">AI Agents</div>
                          <div className="bg-[#0F93B2]/20 rounded px-2 py-1 text-xs text-center font-medium text-slate-800">Digital Agents</div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Knowledge Management Section */}
                <div className="bg-white/80 backdrop-blur rounded-xl p-2.5 border-2 shadow-md border-[rgba(26,180,216,1)]">
                  <div className="flex flex-col lg:flex-row gap-2.5">
                    <div className="lg:w-44 flex-shrink-0">
                      <h3 className="font-display font-bold text-base text-slate-900 mb-0.5">Knowledge</h3>
                      <h3 className="font-display font-bold text-base text-slate-900">Management</h3>
                      <p className="text-xs text-[#276A89] font-medium">KnowMax</p>
                    </div>
                    <div className="flex-1 grid grid-cols-2 md:grid-cols-4 gap-2">
                      <div className="bg-[#0F93B2]/10 rounded-lg p-2 border border-[#276A89]/50">
                        <h4 className="font-semibold text-xs text-slate-800 mb-1 text-center">Knowledge Management</h4>
                        <div className="space-y-0.5">
                          <p className="text-xs text-slate-700 text-center">Decision Trees, Picture Guides, FAQs, Gen-AI Search</p>
                        </div>
                      </div>
                      <div className="bg-[#0F93B2]/10 rounded-lg p-2 border border-[#276A89]/50">
                        <h4 className="font-semibold text-xs text-slate-800 mb-1 text-center">Learning Management</h4>
                        <div className="space-y-0.5">
                          <p className="text-xs text-slate-700 text-center">Learning Paths, Gamification</p>
                        </div>
                      </div>
                      <div className="bg-[#0F93B2]/10 rounded-lg p-2 border border-[#276A89]/50">
                        <h4 className="font-semibold text-xs text-slate-800 mb-1 text-center">Quality Management</h4>
                        <div className="space-y-0.5">
                          <p className="text-xs text-slate-700 text-center">Evaluations, Sentiment Analysis, Rule Builder</p>
                        </div>
                      </div>
                      <div className="bg-[#0F93B2]/10 rounded-lg p-2 border border-[#276A89]/50">
                        <h4 className="font-semibold text-xs text-slate-800 mb-1 text-center">Content PS</h4>
                        <div className="space-y-0.5">
                          <p className="text-xs text-slate-700 text-center">Knowledge Migration</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Performance Management Section */}
                <div className="bg-white/80 backdrop-blur rounded-xl p-2.5 border-2 shadow-md border-[rgba(26,180,216,1)]">
                  <div className="flex flex-col lg:flex-row gap-2.5">
                    <div className="lg:w-44 flex-shrink-0">
                      <h3 className="font-display font-bold text-base text-slate-900 mb-0.5">Performance</h3>
                      <h3 className="font-display font-bold text-base text-slate-900">Management</h3>
                      <p className="text-xs text-[#276A89] font-medium">LeapMax + WorkMax</p>
                    </div>
                    <div className="flex-1 grid grid-cols-1 md:grid-cols-3 gap-2">
                      <div className="bg-[#0F93B2]/10 rounded-lg p-2 border border-[#276A89]/50">
                        <h4 className="font-semibold text-xs text-slate-800 mb-1 text-center">Productivity Management</h4>
                        <div className="space-y-0.5">
                          <p className="text-xs text-slate-700 text-center">App & Web Usage, Productivity Stats, Agent Activity</p>
                        </div>
                      </div>
                      <div className="bg-[#0F93B2]/10 rounded-lg p-2 border border-[#276A89]/50">
                        <h4 className="font-semibold text-xs text-slate-800 mb-1 text-center">Infosec Management</h4>
                        <div className="space-y-0.5">
                          <p className="text-xs text-slate-700 text-center">Device Kiosk Mode, Content Protection, Leap OS Personal & Official, Restrict usage</p>
                        </div>
                      </div>
                      <div className="bg-[#0F93B2]/10 rounded-lg p-2 border border-[#276A89]/50">
                        <h4 className="font-semibold text-xs text-slate-800 mb-1 text-center">Compliance Management</h4>
                        <div className="space-y-0.5">
                          <p className="text-xs text-slate-700 text-center">Profanity Filter, Screen Recorder, AI based SOP compliance</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Moving Physical to Digital Management Section */}
                <div className="bg-white/80 backdrop-blur rounded-xl p-2.5 border-2 shadow-md border-[rgba(26,180,216,1)]">
                  <div className="flex flex-col lg:flex-row gap-2.5">
                    <div className="lg:w-44 flex-shrink-0">
                      <h3 className="font-display font-bold text-base text-slate-900 mb-0.5">Moving Physical to</h3>
                      <h3 className="font-display font-bold text-base text-slate-900">Digital Management</h3>
                      <p className="text-xs text-[#276A89] font-medium">DeviceMax</p>
                    </div>
                    <div className="flex-1 grid grid-cols-2 md:grid-cols-4 gap-2">
                      <div className="bg-[#0F93B2]/10 rounded-lg p-2 border border-[#276A89]/50">
                        <h4 className="font-semibold text-xs text-slate-800 mb-1 text-center">Business Efficiency</h4>
                        <div className="space-y-0.5">
                          <p className="text-xs text-slate-700 text-center">Revenue Enhancement, People Management, Pilferage Control</p>
                        </div>
                      </div>
                      <div className="bg-[#0F93B2]/10 rounded-lg p-2 border border-[#276A89]/50">
                        <h4 className="font-semibold text-xs text-slate-800 mb-1 text-center">Sustainability</h4>
                        <div className="space-y-0.5">
                          <p className="text-xs text-slate-700 text-center">Energy Efficiency, Wastage Control, Reduced Carbon Footprint</p>
                        </div>
                      </div>
                      <div className="bg-[#0F93B2]/10 rounded-lg p-2 border border-[#276A89]/50">
                        <h4 className="font-semibold text-xs text-slate-800 mb-1 text-center">Safety & Security</h4>
                        <div className="space-y-0.5">
                          <p className="text-xs text-slate-700 text-center">CCTV Surveillance, Intrusion Alarm System, Fire Alarm System, Access Control</p>
                        </div>
                      </div>
                      <div className="bg-[#0F93B2]/10 rounded-lg p-2 border border-[#276A89]/50">
                        <h4 className="font-semibold text-xs text-slate-800 mb-1 text-center">Video Analytics</h4>
                        <div className="space-y-0.5">
                          <p className="text-xs text-slate-700 text-center">Video Analytics, Edge or on-cloud analytics, Cloud Video Storage</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* MAX-AI Suite Section */}
                <div className="bg-orange-400/20 rounded-xl p-2 shadow-md border-2 border-orange-600">
                  <div className="flex flex-wrap items-center justify-center gap-2">
                    <div className="bg-white rounded px-2.5 py-1">
                      <p className="font-medium text-xs text-orange-600">MAX-AI Suite</p>
                    </div>
                    <div className="bg-white rounded px-2.5 py-1">
                      <p className="text-xs font-medium text-slate-700">AI - Agents</p>
                    </div>
                    <div className="bg-white rounded px-2.5 py-1">
                      <p className="text-xs font-medium text-slate-700">Conversational Chat Bots</p>
                    </div>
                    <div className="bg-white rounded px-2.5 py-1">
                      <p className="text-xs font-medium text-slate-700">Video Bots</p>
                    </div>
                    <div className="bg-white rounded px-2.5 py-1">
                      <p className="text-xs font-medium text-slate-700">Content Generation</p>
                    </div>
                    <div className="bg-white rounded px-2.5 py-1">
                      <p className="text-xs font-medium text-slate-700">Wall-Gardened LLMs</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Right Sidebar - Touch Points, Use Cases, Enterprise Integrations */}
              <div className="lg:w-56 space-y-2.5">
                {/* Touch Points */}
                <div className="bg-orange-400/20 rounded-xl p-2.5 shadow-md border-2 border-orange-600">
                  <h3 className="font-display font-bold text-sm text-conversion mb-2 text-center">Touch Points</h3>
                  <div className="space-y-1.5">
                    <div className="rounded px-2.5 py-1.5 text-center bg-white border border-[#276A89]/50">
                      <p className="text-xs font-semibold text-slate-800">Assisted</p>
                    </div>
                    <div className="rounded px-2.5 py-1.5 text-center bg-white border border-[#276A89]/50">
                      <p className="text-xs font-semibold text-slate-800">Digital</p>
                    </div>
                    <div className="rounded px-2.5 py-1.5 text-center bg-white border border-[#276A89]/50">
                      <p className="text-xs font-semibold text-slate-800">Physical</p>
                    </div>
                    <div className="rounded px-2.5 py-1.5 text-center bg-white border border-[#276A89]/50">
                      <p className="text-xs font-semibold text-slate-800">AI</p>
                    </div>
                  </div>
                </div>

                {/* Use Cases */}
                <div className="bg-orange-400/20 rounded-xl p-2.5 shadow-md mt-[60px] pt-5 pb-2.5 border-2 border-orange-600">
                  <h3 className="font-display font-bold text-sm text-conversion mb-2 text-center">Use Cases</h3>
                  <div className="space-y-1.5">
                    <div className="rounded px-2.5 py-1.5 text-center bg-white border border-[#276A89]/50">
                      <p className="text-xs font-semibold text-slate-800">D2C Acquisition</p>
                    </div>
                    <div className="rounded px-2.5 py-1.5 text-center bg-white border border-[#276A89]/50">
                      <p className="text-xs font-semibold text-slate-800">LTV Management</p>
                    </div>
                  </div>
                </div>

                {/* Enterprise Integrations */}
                <div className="bg-orange-400/20 rounded-xl p-2.5 shadow-md mt-[60px] pt-[15px] pb-2.5 border-2 border-orange-600">
                  <h3 className="font-display font-bold text-sm text-conversion mb-2 text-center">Enterprise</h3>
                  <h3 className="font-display font-bold text-sm text-conversion mb-2 text-center">Integrations</h3>
                  <div className="bg-white rounded-lg p-3">
                    <div className="space-y-2">
                      <div className="flex items-center justify-center gap-2">
                        <AppImage
                          src="/assets/images/image-1770191014910.png"
                          alt="GENESYS logo"
                          width={80}
                          height={24}
                          className="object-contain" />

                      </div>
                      <div className="flex items-center justify-center gap-2 mt-[5px]">
                        <div className="flex items-center gap-1">
                          <AppImage
                            src="/assets/images/image-1770190413963.png"
                            alt="Zendesk logo"
                            width={80}
                            height={24}
                            className="object-contain" />

                        </div>
                      </div>
                      <div className="flex items-center justify-center gap-2 mt-[5px]">
                        <AppImage
                          src="/assets/images/image-1770190503428.png"
                          alt="Exotel logo"
                          width={80}
                          height={24}
                          className="object-contain" />

                        <AppImage
                          src="/assets/images/image-1770190624072.png"
                          alt="Salesforce logo"
                          width={80}
                          height={24}
                          className="object-contain" />

                      </div>
                      <div className="flex items-center justify-center gap-2">
                        <AppImage
                          src="/assets/images/image-1770190684091.png"
                          alt="Freshworks logo"
                          width={80}
                          height={24}
                          className="object-contain" />

                      </div>
                      <div className="flex items-center justify-center gap-2">
                        <AppImage
                          src="/assets/images/image-1770190787718.png"
                          alt="SAP Logo"
                          width={80}
                          height={24}
                          className="object-contain" />

                        <AppImage
                          src="/assets/images/image-1770190941939.png"
                          alt="Globitel logo"
                          width={80}
                          height={24}
                          className="object-contain" />

                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Problem Statement */}
          <div className="mb-12">
            <div className="max-w-5xl mx-auto">
              <h3 className="font-display font-bold text-2xl lg:text-3xl text-center text-foreground mb-8 leading-tight">
                Most businesses operate with disconnected tools:
              </h3>
              
              {/* Problem Cards */}
              <div className="grid md:grid-cols-2 gap-6 mb-8 max-w-[900px] mx-auto">
                <div className="bg-[#fafbfd] border border-border rounded-lg p-6 shadow-lg rounded-br-[10px] rounded-t-[10px] rounded-bl-[10px]">
                  <div className="flex items-start space-x-3">
                    <Icon name="XMarkIcon" size={24} className="text-white bg-black p-1 rounded-[50%] mt-1 flex-shrink-0" />
                    <div><p className="font-medium text-xl text-black">
                      Knowledge lives in one place, agents work in another
                    </p> <p className="font-body text-base text-muted-foreground">
                   Information is siloed, teams are out of sync  
                    </p></div>
                  </div>
                   <img src="https://images.unsplash.com/photo-1562852710-0124584a2791" alt="image area" className="max-w-full mt-4" />
                </div>
                
                <div className="bg-[#fafbfd] border border-border rounded-lg p-6 shadow-lg rounded-br-[10px] rounded-t-[10px] rounded-bl-[10px]">
                  <div className="flex items-start space-x-3">
                    <Icon name="XMarkIcon" size={24} className="text-white bg-black p-1 rounded-[50%] mt-1 flex-shrink-0" />
                    <div> <p className="font-medium text-xl text-black">
                      AI runs in islation, performance is measured too late
                    </p>
                    <p className="font-body text-base text-muted-foreground">
                    Insights are isolated and delayed.
                    </p></div>
                  </div>
                  <img src="/assets/images/New_Project_1_-1771916199332.jpg" alt="image area" className="max-w-full mt-4" />
                </div>
              </div>
              
              {/* Result */}
              <div className="max-w-3xl mx-auto">
                <p className="text-center font-display font-semibold text-2xl lg:text-3xl text-[#FF6B35]">
                  The result? Lost opportunities, inconsistent engagement, and growth that doesn't compound.
                </p>
              </div>
            </div>
          </div>

          {/* Solution Introduction */}
          <div className="text-center mb-12">
            <div className="inline-flex items-center space-x-2 bg-primary/10 rounded-full px-6 py-3 mb-6">
              <Icon name="SparklesIcon" size={32} variant="solid" className="text-primary" />
              <span className="font-body text-lg text-primary"><span className="text-black font-semibold ">UnifyMax</span> changes that.</span>
            </div>
            <div className="bg-gradient-to-br from-blue-50 to-indigo-50 border-2 border-blue-200 rounded-2xl p-8 shadow-lg max-w-4xl mx-auto">
              <p className="font-heading text-xl lg:text-2xl font-bold text-primary leading-relaxed">
                Great CX is no longer about tools. It's orchestrated through a single, intelligent system that eliminates silos.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>);

};

export default TransformationPathway;